import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";

const PRIMARY = "#363C90";

const getDynamicSlots = () => {
  const today = new Date();
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
    });
  };

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const dayAfter = new Date(today);
  dayAfter.setDate(today.getDate() + 2);

  return [
    { id: "1", day: "Tomorrow", dateLabel: formatDate(tomorrow), time: "10 AM – 1 PM", period: "Morning" },
    { id: "2", day: "Tomorrow", dateLabel: formatDate(tomorrow), time: "1 PM – 4 PM", period: "Afternoon" },
    { id: "3", day: "Tomorrow", dateLabel: formatDate(tomorrow), time: "4 PM – 7 PM", period: "Evening" },
    { id: "4", day: formatDate(dayAfter), dateLabel: formatDate(dayAfter), time: "10 AM – 1 PM", period: "Morning" },
    { id: "5", day: formatDate(dayAfter), dateLabel: formatDate(dayAfter), time: "1 PM – 4 PM", period: "Afternoon" },
  ];
};

const Slot: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = location.state || {};
  
  const [selected, setSelected] = useState<string | null>(null);
  const dynamicSlots = useMemo(() => getDynamicSlots(), []);

  const handleContinue = () => {
    if (!selected) return;
    const chosen = dynamicSlots.find(s => s.id === selected);
    const slotLabel = `${chosen?.day === 'Tomorrow' ? chosen?.dateLabel : chosen?.day} (${chosen?.time})`;
    
    navigate("/sell/payment", { 
      state: { ...params, pickupSlot: slotLabel } 
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      
      {/* --- COMPACT DASHBOARD HEADER --- */}
      <div className="bg-white border-b border-slate-100 py-6 px-6">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
           <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight leading-none">Pickup Slot</h1>
              <p className="text-slate-400 text-[10px] font-black mt-2 uppercase tracking-[2px] flex items-center gap-1.5 leading-none">
                <Calendar size={12} className="text-[#363C90]" /> Choose your convenience
              </p>
           </div>
           <div className="hidden md:flex items-center gap-2 bg-emerald-50 text-emerald-600 px-3 py-1.5 rounded-xl border border-emerald-100">
              <ShieldCheck size={14} />
              <span className="text-[9px] font-black uppercase tracking-widest">Verified Pickup</span>
           </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT: SLOTS GRID */}
          <div className="lg:col-span-8">
             <div className="flex items-center justify-between px-2 mb-4">
                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Available Timings</h3>
                <span className="text-[10px] font-bold text-slate-300 italic">Next 48 Hours</span>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {dynamicSlots.map((item) => {
                  const isActive = selected === item.id;
                  return (
                    <div
                      key={item.id}
                      onClick={() => setSelected(item.id)}
                      className={`group p-5 rounded-2xl cursor-pointer border transition-all duration-200 relative overflow-hidden
                        ${isActive 
                          ? "bg-white border-[#363C90] shadow-lg ring-1 ring-[#363C90]" 
                          : "bg-white border-slate-100 hover:border-slate-200 shadow-sm"}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                         <div className="flex flex-col">
                            <span className={`text-[10px] font-black uppercase tracking-widest ${isActive ? "text-[#363C90]" : "text-slate-400"}`}>
                               {item.day === "Tomorrow" ? "Tomorrow" : "Scheduled"}
                            </span>
                            <span className={`text-sm font-black mt-0.5 ${isActive ? "text-slate-900" : "text-slate-700"}`}>
                               {item.dateLabel}
                            </span>
                         </div>
                         {isActive && <CheckCircle2 className="text-[#363C90]" size={18} />}
                      </div>

                      <div className={`flex items-center gap-2 p-2.5 rounded-xl transition-colors ${isActive ? "bg-indigo-50 text-[#363C90]" : "bg-slate-50 text-slate-500"}`}>
                         <Clock size={14} />
                         <span className="text-[11px] font-black tracking-tight">{item.time}</span>
                         <span className="ml-auto text-[8px] font-black uppercase opacity-60 tracking-widest">{item.period}</span>
                      </div>
                    </div>
                  );
                })}
             </div>
          </div>

          {/* RIGHT: SUMMARY & ACTION */}
          <div className="lg:col-span-4 lg:sticky lg:top-8">
            <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
               <h3 className="text-sm font-black text-slate-900 mb-6 uppercase tracking-tight">Booking Summary</h3>
               
               <div className="space-y-4 mb-8">
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-50">
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-3">Your Selection:</p>
                    {selected ? (
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 bg-[#363C90] text-white rounded-xl flex items-center justify-center">
                            <Clock size={20} />
                         </div>
                         <div>
                            <p className="text-xs font-black text-slate-900 leading-none">
                               {dynamicSlots.find(s => s.id === selected)?.time}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">
                               {dynamicSlots.find(s => s.id === selected)?.dateLabel}
                            </p>
                         </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-slate-400 font-medium italic">Select a slot to proceed...</p>
                    )}
                  </div>

                  <div className="flex gap-2 items-start px-1 opacity-70">
                    <ShieldCheck size={14} className="text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-[9px] text-slate-500 font-bold leading-relaxed uppercase">
                      Executive will call 30 mins before arrival.
                    </p>
                  </div>
               </div>

               <button
                 disabled={!selected}
                 onClick={handleContinue}
                 className="w-full py-5 bg-[#363C90] text-white rounded-2xl font-black text-[11px] flex items-center justify-center gap-3 disabled:opacity-30 disabled:grayscale shadow-xl shadow-indigo-900/10 hover:translate-y-[-2px] transition-all uppercase tracking-[2px]"
               >
                 PROCEED TO PAYMENT <ArrowRight size={16} />
               </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Slot;